import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FilePlus2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectNoneItem,
  SelectTrigger,
  SelectValue,
  clearable,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ListFilterBar from "@/components/ListFilterBar";
import DocumentNumberLink from "@/components/DocumentNumberLink";
import Pagination from "@/components/Pagination";
import { usePagination } from "@/hooks/usePagination";
import { useAuth } from "@/auth";
import {
  useClearFilterParams,
  useFilterParam,
} from "@/hooks/useFilterParam";
import {
  CURRENCY,
  JOURNAL_STATUS_BADGE,
  type GlJournalListRow,
} from "@/lib/gl";

const SOURCES = ["all", "manual", "sales_invoice", "receipt", "adjustment"] as const;

function fmtMoney(n: number) {
  return `${CURRENCY} ${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function GeneralLedgerJournals() {
  const { can } = useAuth();
  const canWrite = can("gl.write");
  const canView = can("gl.view");
  const [rows, setRows] = useState<GlJournalListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusTab, setStatusTab] = useFilterParam("status");
  const [q, setQ] = useFilterParam("q");
  const [docNo, setDocNo] = useFilterParam("doc_no");
  const [dateFrom, setDateFrom] = useFilterParam("date_from");
  const [dateTo, setDateTo] = useFilterParam("date_to");
  const [source, setSource] = useState<string>("all");
  const clearFilters = useClearFilterParams([
    "q",
    "doc_no",
    "status",
    "date_from",
    "date_to",
  ]);

  const activeStatusTab = statusTab || "all";

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (activeStatusTab !== "all") qs.set("status", activeStatusTab);
      if (dateFrom) qs.set("from", dateFrom);
      if (dateTo) qs.set("to", dateTo);
      if (source && source !== "all") qs.set("source", source);
      if (q.trim()) qs.set("q", q.trim());
      const s = qs.toString();
      const d = await api.get<{ journals: GlJournalListRow[] }>(
        `/gl/journals${s ? `?${s}` : ""}`,
      );
      setRows(d.journals);
    } catch (err) {
      toast.error((err as Error).message || "Failed to load journals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStatusTab, dateFrom, dateTo, source, q]);

  // Journal reference is filtered client-side so users can narrow by exact
  // reference substring without re-querying on every keystroke in that field.
  const displayed = useMemo(() => {
    const refNeedle = docNo.trim().toLowerCase();
    if (!refNeedle) return rows;
    return rows.filter((j) =>
      j.reference.toLowerCase().includes(refNeedle),
    );
  }, [rows, docNo]);

  const counts = useMemo(() => {
    const c = { all: displayed.length, draft: 0, posted: 0, reversed: 0, void: 0 };
    for (const r of displayed) {
      if (r.status in c) c[r.status as keyof typeof c] += 1;
    }
    return c;
  }, [displayed]);

  const hasAnyFilter = Boolean(
    q || docNo || (statusTab && statusTab !== "all") || dateFrom || dateTo,
  );

  const pg = usePagination(displayed);

  useEffect(() => {
    pg.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStatusTab, dateFrom, dateTo, source, q, docNo]);

  return (
    <div className="space-y-4">
      <Card className="shadow-card">
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-lg">Journal Entries</CardTitle>
            <CardDescription>
              Post and manage balanced double-entry journals. Posted entries are
              immutable — reverse them to undo.
            </CardDescription>
          </div>
          {canWrite && (
            <Button asChild>
              <Link to="/general-ledger/journals/new">
                <FilePlus2 className="h-4 w-4 mr-1" />
                New Journal
              </Link>
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <Tabs
            value={activeStatusTab}
            onValueChange={(v) => setStatusTab(v === "all" ? "" : v)}
          >
            <TabsList className="grid grid-cols-5 w-full max-w-2xl">
              <TabsTrigger value="all">All ({counts.all})</TabsTrigger>
              <TabsTrigger value="draft">Drafts ({counts.draft})</TabsTrigger>
              <TabsTrigger value="posted">Posted ({counts.posted})</TabsTrigger>
              <TabsTrigger value="reversed">
                Reversed ({counts.reversed})
              </TabsTrigger>
              <TabsTrigger value="void">Void ({counts.void})</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="max-w-xs">
            <Label htmlFor="source">Source</Label>
            <Select value={source} onValueChange={clearable(setSource)}>
              <SelectTrigger id="source" className="h-10">
                <SelectValue placeholder="All sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectNoneItem />
                {SOURCES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s === "all" ? "All sources" : s.replace(/_/g, " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[280px_minmax(0,1fr)]">
        <ListFilterBar
          title="Search Journal"
          searchValue={q}
          onSearchChange={setQ}
          searchPlaceholder="Reference or description"
          docNo={{
            value: docNo,
            onChange: setDocNo,
            placeholder: "Journal Ref",
          }}
          dateFrom={{ value: dateFrom, onChange: setDateFrom }}
          dateTo={{ value: dateTo, onChange: setDateTo }}
          hasAnyFilter={hasAnyFilter}
          onClear={clearFilters}
        />
        <Card className="shadow-card">
          <CardContent className="p-0">
            <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Reference</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Debit</TableHead>
                <TableHead className="text-right">Credit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : displayed.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                    No journals match these filters.
                  </TableCell>
                </TableRow>
              ) : (
                pg.pageItems.map((j) => (
                  <TableRow key={j.id}>
                    <TableCell className="font-mono">
                      <DocumentNumberLink
                        to={`/general-ledger/journals/${j.id}`}
                        canOpen={canView}
                      >
                        {j.reference}
                      </DocumentNumberLink>
                    </TableCell>
                    <TableCell>{j.entry_date}</TableCell>
                    <TableCell className="max-w-[36ch] truncate">
                      {j.description || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {j.source.replace(/_/g, " ")}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={`capitalize ${JOURNAL_STATUS_BADGE[j.status] ?? ""}`}
                      >
                        {j.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {fmtMoney(j.total_debit)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {fmtMoney(j.total_credit)}
                    </TableCell>
                  </TableRow>
                ))
              )}
              </TableBody>
            </Table>
            {!loading && displayed.length > 0 ? (
              <Pagination
                page={pg.page}
                pageCount={pg.pageCount}
                total={pg.total}
                from={pg.from}
                to={pg.to}
                onPrev={pg.prev}
                onNext={pg.next}
              />
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
